import { Box, Button, Stack, Tooltip } from "@mui/material";
import {
  Article,
  AutoAwesome,
  ContentPasteGo,
  Done,
  History,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useState } from "react";

import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useFhirData } from "../../../../utils/useFhirData";
import { useFormatter } from "../../../../utils/useFormatter";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "../RegisterArea/useFilteredMedicalData";
import { generateMedicalTextAndCopy } from "@/utils/medicalTextBuilder";
import VideoConferencia from "../../../../components/videoConferencia/VideoConferencia";
import ChatMedPaciente from "../../../../components/videoConferencia/ChatMedPaciente";
import { PrimaryButton } from "../../../../components/common";
import { isEvolutionAppointment } from "@/components/schedule/evolution/isEvolutionAppointment";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import {
  InviteTeleconsultoriaButton,
  readWherebyRoom,
  teleconsultoriaGuestOfAppointment,
} from "src-next/modules/teleconsultoria";
import { IpesEmbeddedPortalAccess } from "src-next/modules/ipes-embedded-portal";

/**
 * Barra de ações do atendimento.
 * Reúne, em uma única linha logo abaixo dos dados do paciente (Header),
 * os botões que antes ficavam espalhados entre StepArea, Header e RegisterArea:
 * 1) Iniciar Teleatendimento (ou chat)  2) Sumário de saúde
 * 3) Informações de outras redes (IPES) 4) Questionários do paciente
 * 5) Análise com I.A. (Luxor)
 */
export function ActionBar() {
  const { formatGender, formatAge } = useFormatter;
  const { state } = useLocation();
  const [invitedRoomUrl, setInvitedRoomUrl] = useState<string | undefined>();
  const { contractTeleconsultoria } = useTeleconsultoriaContractContext();

  const {
    activeStep,
    setActiveStep,
    patient,
    appointment,
    encounter,
    clinicalLocation,
    setOpenSummary,
    luxorMode,
    setLuxorMode,
    handleSubmit,
    condutas,
    problems,
    setCopyMedicalData,
    isPatientQuestionnairesPanelOpen,
    setIsPatientQuestionnairesPanelOpen,
    finalizeAttendanceRef,
    isFinalizingAttendance,
  } = useMedicalCareContext();

  // Última etapa (Documentos e assinatura): botão fica habilitado e pulsante.
  const isLastStep = activeStep === 2;

  // Progresso do botão "Finalizar" conforme a aba atual.
  // Etapa 0 = 0%, etapa 1 = 50%, etapa 2 (Documentos) = 100% (completa e habilita).
  const finalizeFillPercent = isLastStep
    ? "100%"
    : activeStep === 1
      ? "50%"
      : "0%";

  // ----- Teleatendimento / Chat (lógica migrada do StepArea) -----
  const virtualService = useFhirData.virtualService(clinicalLocation);

  // Canal de atendimento: locations sem a extension são tratadas como teleatendimento.
  const attendanceChannel = clinicalLocation?.extension?.find((ext) =>
    ext.url?.includes("attendance-channel")
  )?.valueCode as string | undefined;
  const isChat = attendanceChannel === "chat";
  const isEvolution = isEvolutionAppointment(appointment);
  const evolutionRoomUrl =
    invitedRoomUrl ||
    readWherebyRoom(appointment?.virtualService)?.addressUrl ||
    (isEvolution ? state?.encounterReference?.virtualService : undefined);
  // Teleconsultoria fora de evolução: sala só quando há teleconsultor (convidado agora
  // ou já gravado com CON). Evita mostrar vídeo por um virtualService antigo.
  const teleconsultoriaRoomUrl =
    invitedRoomUrl ||
    (teleconsultoriaGuestOfAppointment(appointment).practitioner
      ? readWherebyRoom(appointment?.virtualService)?.addressUrl
      : undefined);

  // CPF do paciente para derivar os peer IDs da sala P2P do chat.
  const patientCpfForChat: string =
    state?.encounterReference?.subject || state?.patientId || "";

  // ----- Análise com I.A. (Luxor) (lógica migrada do Header) -----
  const filteredConditions = useFilteredConditions();
  const filteredAllergies = useFilteredAllergies();
  const filteredImmunizations = useFilteredImmunizations();
  const textoInterventions = useFilteredSelectedInterventions();

  const obterInformacoesPaciente = () => {
    const educacaoExtension = patient?.extension?.find(
      (el: any) =>
        el.url === "http://terminology.hl7.org/ValueSet/v3-EducationLevel"
    );
    const escolaridade =
      educacaoExtension?.valueCodeableConcept?.coding?.[0]?.display || "-";

    return {
      idade: patient?.birthDate
        ? `${formatAge(`${patient?.birthDate}`)} anos`
        : "-",
      sexo: formatGender(patient?.gender) || "-",
      escolaridade,
    };
  };

  const onSubmit = (data: any) => {
    const dataComContexto = {
      ...data,
      New_Conduta: condutas,
      problems: problems,
    };
    generateMedicalTextAndCopy({
      data: dataComContexto,
      infosPaciente: obterInformacoesPaciente(),
      filteredConditions,
      filteredAllergies,
      filteredImmunizations,
      textoInterventions,
      setContent: (textoFinal) => setCopyMedicalData(textoFinal),
      onSuccess: () =>
        toast.success("Dados de atendimento copiados com sucesso!"),
      onError: () => toast.error("Erro ao copiar dados!"),
    });
  };

  const handleLuxorClick = () => {
    if (!luxorMode) {
      handleSubmit(onSubmit)();
    }
    setLuxorMode(!luxorMode);
  };

  // Nome da tela para a qual o botão de análise volta ao ser desativado.
  // Reflete a etapa atual (0 = Histórico do paciente, 1 = Intervenções).
  const screenReturnLabel =
    activeStep === 1 ? "Intervenções" : "Histórico do paciente";

  // Estilo compartilhado dos botões: mesma altura, cantos arredondados e
  // sem quebra de texto interna, para uma toolbar visualmente consistente.
  const actionButtonSx = {
    textTransform: "none" as const,
    flexShrink: 0,
    borderRadius: 2,
    minHeight: 38,
    whiteSpace: "nowrap" as const,
    px: 1.75,
  };

  return (
    // Sem padding horizontal para alinhar com o RegisterArea (conteúdo em x=0).
    <Box sx={{ py: 0.5 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={1.25}
        sx={{ width: "100%" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          // Só os botões da esquerda quebram. Finalizar permanece na primeira linha.
          flexWrap="wrap"
          useFlexGap
          gap={1.25}
          sx={{ flex: 1, minWidth: 0 }}
        >
        {/* 1) Teleatendimento: não pode quebrar, fica sempre em uma única linha */}
        {virtualService && (
          <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: 220 } }}>
            {isChat ? (
              <ChatMedPaciente patientCpf={patientCpfForChat} role="doctor" />
            ) : (
              <VideoConferencia
                roomUrl={
                  state?.encounterReference?.virtualService || evolutionRoomUrl
                }
                appointmentData={{
                  id: state?.encounterReference?.id,
                  patientName: state?.encounterReference?.subject?.display,
                  practitionerName:
                    state?.encounterReference?.participant?.[0]?.individual
                      ?.display,
                  startTime: state?.encounterReference?.period?.start,
                }}
              />
            )}
          </Box>
        )}

        {/* Evolução com teleconsultoria: sala Whereby mesmo se o local não for virtual. */}
        {isEvolution && !virtualService && evolutionRoomUrl && (
          <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: 220 } }}>
            <VideoConferencia
              roomUrl={evolutionRoomUrl}
              appointmentData={{
                id: state?.encounterReference?.id,
                patientName: state?.encounterReference?.subject?.display,
                practitionerName:
                  state?.encounterReference?.participant?.[0]?.individual
                    ?.display,
                startTime: state?.encounterReference?.period?.start,
              }}
            />
          </Box>
        )}

        {/* Teleconsultoria em consulta comum: presencial/domicílio (sem botão de vídeo)
            e chat (só tem o chat). No teleatendimento por vídeo, o botão acima já usa a mesma sala. */}
        {!isEvolution &&
          teleconsultoriaRoomUrl &&
          (!virtualService || isChat) && (
            <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: 220 } }}>
              <VideoConferencia
                roomUrl={teleconsultoriaRoomUrl}
                appointmentData={{
                  id: state?.encounterReference?.id,
                  patientName: state?.encounterReference?.subject?.display,
                  practitionerName:
                    state?.encounterReference?.participant?.[0]?.individual
                      ?.display,
                  startTime: state?.encounterReference?.period?.start,
                }}
              />
            </Box>
          )}

        {/* Qualquer tipo de atendimento, desde que a extensão esteja habilitada. */}
        {/* Atendimento do teleconsultor (partOf): ele não convida outro teleconsultor. */}
        {appointment?.id &&
          contractTeleconsultoria &&
          !state?.encounterReference?.partOf && (
          <InviteTeleconsultoriaButton
            appointment={appointment}
            encounterId={encounter?.id || state?.encounterReference?.id}
            onRoomReady={setInvitedRoomUrl}
          />
        )}

        {/* 2) Sumário de saúde */}
        <Button
          variant="outlined"
          color="primary"
          sx={actionButtonSx}
          startIcon={<History />}
          onClick={() => setOpenSummary(true)}
        >
          Sumário de saúde
        </Button>

        {/* 3) Informações de outras redes (portal IPES embarcado) */}
        <IpesEmbeddedPortalAccess
          patient={patient}
          patientCpfFallbacks={[patientCpfForChat, state?.patientId]}
          buttonSx={actionButtonSx}
        />

        {/* 4) Questionários do paciente (estado compartilhado via contexto) */}
        <Tooltip title="Ver respostas de questionários do paciente">
          <Button
            color="primary"
            startIcon={<ContentPasteGo fontSize="small" />}
            variant={isPatientQuestionnairesPanelOpen ? "contained" : "outlined"}
            sx={actionButtonSx}
            onClick={() =>
              setIsPatientQuestionnairesPanelOpen(
                !isPatientQuestionnairesPanelOpen
              )
            }
          >
            Questionários do paciente
          </Button>
        </Tooltip>

        {/* 5) Análise com I.A. (Luxor) — não exibido na etapa de assinatura.
            O texto muda conforme a tela: quando a análise está ativa, o botão
            mostra o nome da tela para a qual ele volta (Histórico/Intervenções). */}
        {activeStep !== 2 && (
          <Tooltip
            title={
              luxorMode
                ? `Voltar para ${screenReturnLabel}`
                : "Analisar atendimento com IA"
            }
          >
            <Button
              variant={luxorMode ? "contained" : "outlined"}
              color={luxorMode ? "primary" : "secondary"}
              sx={actionButtonSx}
              startIcon={
                luxorMode ? (
                  <Article />
                ) : (
                  <AutoAwesome fontSize="small" />
                )
              }
              onClick={handleLuxorClick}
            >
              {luxorMode ? screenReturnLabel : "Análise com I.A."}
            </Button>
          </Tooltip>
        )}

        </Stack>

        {/* Finalizar atendimento: fora do wrap, sempre na primeira linha, à direita.
            - Cada aba preenche +30% (etapa 0=30%, 1=60%, 2=100%).
            - Fica desabilitado até a última etapa (só a barra avança).
            - Na etapa "Documentos e assinatura" completa 100%, habilita e pulsa. */}
        <Box
          sx={{
            flexShrink: 0,
            borderRadius: 2,
            // Pulso verde: ativo somente na última etapa (botão habilitado).
            "@keyframes finalizePulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(0, 124, 109, 0.45)" },
              "70%": { boxShadow: "0 0 0 10px rgba(0, 124, 109, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(0, 124, 109, 0)" },
            },
            ...(isLastStep && {
              animation: "finalizePulse 1.8s ease-in-out infinite",
            }),
            // Estilo base do botão: track claro + barra verde crescendo à esquerda.
            "& .MuiButtonBase-root": {
              border: "1.5px solid #007C6D",
              // Track (fundo não preenchido) em verde bem claro.
              backgroundColor: "rgba(0, 124, 109, 0.12)",
              // Barra de progresso: gradiente verde sólido dimensionado por etapa.
              backgroundImage: "linear-gradient(#007C6D, #007C6D)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              backgroundSize: `${finalizeFillPercent} 100%`,
              // Transição suave a cada avanço de aba (efeito de "encher").
              transition: "background-size 0.6s ease",
              // Texto: branco na etapa cheia; verde escuro (legível) nas demais.
              color: isLastStep ? "#fff" : "#0A473F",
            },
            // Mantém a aparência de barra mesmo desabilitado (sem ficar cinza).
            "& .MuiButtonBase-root.Mui-disabled": {
              opacity: 1,
              border: "1.5px solid #007C6D",
              backgroundColor: "rgba(0, 124, 109, 0.12)",
              backgroundImage: "linear-gradient(#007C6D, #007C6D)",
              backgroundSize: `${finalizeFillPercent} 100%`,
              color: "#0A473F",
            },
            // Hover só faz sentido na etapa habilitada; escurece um pouco.
            "& .MuiButtonBase-root:hover": {
              ...(isLastStep && {
                backgroundImage: "linear-gradient(#006055, #006055)",
                borderColor: "#006055",
              }),
            },
          }}
        >
          <PrimaryButton
            width="260px"
            height="38px"
            endIcon={<Done />}
            disabled={!isLastStep}
            loading={isFinalizingAttendance}
            onClick={() => finalizeAttendanceRef.current()}
          >
            Finalizar atendimento
          </PrimaryButton>
        </Box>
      </Stack>
    </Box>
  );
}
